/**
 * Data View Tab for Demo Admin
 *
 * Displays atomic shift-daily demo data with filters, charts, and tables
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { API } from 'aws-amplify';
import AtomicDataFilters from '../components/AtomicDataFilters';
import FilterDataPanel from '../components/FilterDataPanel';
import ConfigurationPanel from '../components/ConfigurationPanel';
import MetadataStatsCard from '../components/MetadataStatsCard';
import MetricTrendCharts from '../components/MetricTrendCharts';
import AtomicDataTable from '../components/AtomicDataTable';
import { LIST_LOCATIONS_BY_CLIENT, LIST_DEMO_ATOMIC_SHIFT_DAILIES, GET_DEMO_DATA_DATE_BOUNDS } from '../queries';
import { aggregateDailyData } from '../utils/aggregateDailyData';

/**
 * Data View Tab Component
 * Provides filtering, visualization, and data exploration for demo data
 * @param {Object} props - Component props
 * @param {string} props.selectedClient - Currently selected client ID
 */
const DataViewTab = ({ selectedClient }) => {
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [atomicData, setAtomicData] = useState([]); // Raw shift-level data from API
  const [dateBounds, setDateBounds] = useState({ minDate: null, maxDate: null });
  const [dateBoundsLoading, setDateBoundsLoading] = useState(false);

  // Client-side filter state
  const [filterLocationIds, setFilterLocationIds] = useState([]);
  const [filterShift, setFilterShift] = useState('');

  // Fetch locations when selectedClient changes
  useEffect(() => {
    const fetchLocations = async () => {
      if (!selectedClient) {
        setLocations([]);
        return;
      }

      try {
        setLocationsLoading(true);
        setLocationsError(null);

        // Fetch all locations (including closed ones) with pagination
        let allLocations = [];
        let nextToken = null;

        do {
          const result = await API.graphql({
            query: LIST_LOCATIONS_BY_CLIENT,
            variables: {
              clientId: selectedClient,
              limit: 1000,
              nextToken
            }
          });

          const items = result.data.locationByClientId.items || [];
          allLocations = [...allLocations, ...items];
          nextToken = result.data.locationByClientId.nextToken;
        } while (nextToken);

        // Transform to filter format (id, name)
        const transformedLocations = allLocations.map(loc => ({
          id: loc.id,
          name: loc.locationName || loc.locationNumber || loc.id,
          closeDate: loc.closeDate
        }));

        // Sort by name
        transformedLocations.sort((a, b) => a.name.localeCompare(b.name));

        setLocations(transformedLocations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocationsError(error.message || 'Failed to fetch locations');
      } finally {
        setLocationsLoading(false);
      }
    };

    fetchLocations();
  }, [selectedClient]);

  // Fetch date bounds when selectedClient changes
  useEffect(() => {
    const fetchDateBounds = async () => {
      if (!selectedClient) {
        setDateBounds({ minDate: null, maxDate: null });
        return;
      }

      try {
        setDateBoundsLoading(true);

        const result = await API.graphql({
          query: GET_DEMO_DATA_DATE_BOUNDS,
          variables: { clientId: selectedClient }
        });

        const earliestItems = result.data.earliest?.items || [];
        const latestItems = result.data.latest?.items || [];

        const minDate = earliestItems.length > 0 ? earliestItems[0].date : null;
        const maxDate = latestItems.length > 0 ? latestItems[0].date : null;

        setDateBounds({ minDate, maxDate });
      } catch (error) {
        console.error('Error fetching date bounds:', error);
        // Don't set error state - just use default behavior with no bounds
        setDateBounds({ minDate: null, maxDate: null });
      } finally {
        setDateBoundsLoading(false);
      }
    };

    fetchDateBounds();
  }, [selectedClient]);

  /**
   * Get unique shifts from fetched data
   */
  const availableShifts = useMemo(() => {
    const shifts = [...new Set(atomicData.map(item => item.shift))];
    return shifts.sort();
  }, [atomicData]);

  /**
   * Get locations that exist in fetched data
   */
  const fetchedLocationIds = useMemo(() => {
    return [...new Set(atomicData.map(item => item.locationId))];
  }, [atomicData]);

  /**
   * Filter locations to only those in fetched data
   */
  const filteredLocations = useMemo(() => {
    return locations.filter(loc => fetchedLocationIds.includes(loc.id));
  }, [locations, fetchedLocationIds]);

  /**
   * Compute filtered data based on client-side filters
   * This is the raw filtered data (shift-level records)
   */
  const filteredData = useMemo(() => {
    let data = atomicData;

    // Apply location filter
    if (filterLocationIds.length > 0 && filterLocationIds.length < filteredLocations.length) {
      data = data.filter(item => filterLocationIds.includes(item.locationId));
    }

    // Apply shift filter
    if (filterShift) {
      data = data.filter(item => item.shift === filterShift);
    }

    return data;
  }, [atomicData, filterLocationIds, filterShift, filteredLocations.length]);

  /**
   * Compute aggregated data for tiles and charts
   * Always shows aggregates regardless of table view mode
   */
  const aggregatedData = useMemo(() => {
    return aggregateDailyData(filteredData);
  }, [filteredData]);

  /**
   * Handle filter apply
   * @param {Object} filters - Applied filter values
   */
  const handleApplyFilters = async (filters) => {
    console.log('Filters applied:', filters);
    setAppliedFilters(filters);
    setIsLoading(true);
    setFetchError(null);

    try {
      // Build GraphQL filter
      // Note: ModelStringInput doesn't support 'in' operator, so we use 'or' for multiple locations
      let filter = {
        clientId: { eq: selectedClient },
        date: {
          between: [filters.startDate, filters.endDate]
        }
      };

      // Add shift filter if specific shift selected
      if (filters.shift) {
        filter.shift = { eq: filters.shift };
      }

      // Add location filter if specific locations selected
      // Use 'or' at the top level since ModelStringInput doesn't support 'in'
      if (filters.locationIds && filters.locationIds.length > 0) {
        const locationFilters = filters.locationIds.map(id => ({
          ...filter,
          locationId: { eq: id }
        }));
        filter = { or: locationFilters };
      }

      // Fetch data with pagination support
      let allRecords = [];
      let nextToken = null;

      do {
        const result = await API.graphql({
          query: LIST_DEMO_ATOMIC_SHIFT_DAILIES,
          variables: {
            filter,
            limit: 1000, // Max records per page
            nextToken
          }
        });

        const items = result.data.listDemoAtomicShiftDailies.items || [];
        allRecords = [...allRecords, ...items];
        nextToken = result.data.listDemoAtomicShiftDailies.nextToken;

        console.log(`Fetched ${items.length} records (total: ${allRecords.length})`);
      } while (nextToken);

      console.log(`Total records fetched: ${allRecords.length}`);
      setAtomicData(allRecords);
    } catch (error) {
      console.error('Error fetching demo data:', error);
      setFetchError(error.message || 'Failed to fetch demo data');
      setAtomicData([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle filter reset
   */
  const handleResetFilters = () => {
    console.log('Filters reset');
    setAppliedFilters(null);
    setAtomicData([]);
    setFetchError(null);
    // Reset client-side filters
    setFilterLocationIds([]);
    setFilterShift('');
  };

  // Check if client has confirmed data (bounds loaded and dates found)
  const clientHasData = selectedClient && !dateBoundsLoading && (dateBounds.minDate || dateBounds.maxDate);
  // Show no data message when client selected but either still loading or confirmed no data
  const showNoDataMessage = selectedClient && !clientHasData;

  return (
    <Box>
      {/* No data message - Show when client selected but data not yet confirmed */}
      {showNoDataMessage && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {dateBoundsLoading ? (
            <Typography variant="body1" color="text.secondary">
              Loading data...
            </Typography>
          ) : (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED' }}>
                No Demo Data Available
              </Typography>
              <Typography variant="body1" color="text.secondary">
                This client hasn't uploaded any demo data yet. Use the Data Management Tab to create demo data for this client.
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* Show panels only when client has confirmed data or no client selected */}
      {(clientHasData || !selectedClient) && (
        <>
          {/* Configuration Panel */}
          <Paper sx={{ mb: 3 }}>
            <Accordion defaultExpanded={false}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'rgba(124, 58, 237, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(124, 58, 237, 0.08)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon sx={{ color: '#7C3AED' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#7C3AED' }}>
                    Configuration
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <ConfigurationPanel clientId={selectedClient} />
              </AccordionDetails>
            </Accordion>
          </Paper>

          {/* Filters Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED', mb: 2 }}>
              Data Criteria
            </Typography>

            {/* Location fetch error */}
            {locationsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load locations: {locationsError}
              </Alert>
            )}

            {/* Data fetch error */}
            {fetchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {fetchError}
              </Alert>
            )}

            {/* No client selected */}
            {!selectedClient && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select a client to view locations and filter data
              </Alert>
            )}

            <AtomicDataFilters
              locations={locations}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              loading={isLoading || locationsLoading || dateBoundsLoading}
              minDate={dateBounds.minDate}
              maxDate={dateBounds.maxDate}
            />
          </Paper>
        </>
      )}

      {/* Filter Data and Results - Only show when data has been fetched */}
      {appliedFilters && atomicData.length > 0 && (
        <>
          {/* Metadata Stats - always shows aggregated data */}
          <MetadataStatsCard data={aggregatedData} />

          {/* Filter Data Panel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <FilterDataPanel
              locations={filteredLocations}
              selectedLocationIds={filterLocationIds}
              onLocationChange={setFilterLocationIds}
              selectedShift={filterShift}
              onShiftChange={setFilterShift}
              disabled={isLoading}
              availableShifts={availableShifts}
            />
          </Paper>

          {/* Trend Charts - uses aggregated data */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <MetricTrendCharts
              data={aggregatedData}
              loading={isLoading}
            />
          </Paper>

          {/* Data Table - handles its own view mode internally */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED', mb: 2 }}>
              Data Table
            </Typography>

            <AtomicDataTable
              data={filteredData}
              locations={locations}
              loading={isLoading}
            />
          </Paper>
        </>
      )}

      {/* No data message - Only show when filters applied but no data */}
      {appliedFilters && atomicData.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No data available for selected criteria
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DataViewTab;
